import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PlayButton } from '../play/PlayTarget';
import { usePlayTarget } from '../play/usePlayTarget';
import { motion } from 'framer-motion';
import { playLoop } from '../play/playLoop';
import { useFpsBackoff } from '../storm/useFpsBackoff';
import JournalCanvas from '../components/JournalCanvas';
import { Entry } from '../library/entry';

const HOLD_MS = 1200;
const TREMOR_PX = 6; // amplitude at full hold progress

const NOOP = () => {};
const EMPTY_TEXT = [''];

// Hold-to-delete lives in its own component so the 60Hz progress never re-renders the
// overlay (and its JournalCanvas). Progress is written straight to the --hold CSS var;
// state only flips once at the start and end of a hold (for the label).
// Tremor: while holding, the .gj-book (not the stage, which holds this button — moving it would
// slide the button out from under the pointer and onPointerLeave would abort the hold) shakes by
// a rigid translate whose amplitude ramps 0→TREMOR_PX with the hold progress (never a
// filter/skew on the JournalCanvas). Written in a playLoop tick; reset on release. Calm (reduced
// motion) or fps backoff step ≥2: none.
const HoldDeleteButton: React.FC<{ onConfirm: () => void; book: React.RefObject<HTMLDivElement>; calm: boolean }> = ({ onConfirm, book, calm }) => {
  const btn = useRef<HTMLButtonElement>(null);
  const raf = useRef(0);
  const start = useRef(0);
  const active = useRef(false);
  const progress = useRef(0);
  const tremor = useRef<(() => void) | null>(null);
  const [holding, setHolding] = useState(false);

  const stopTremor = () => {
    tremor.current?.(); tremor.current = null;
    if (book.current) book.current.style.transform = '';
  };
  useEffect(() => () => { cancelAnimationFrame(raf.current); tremor.current?.(); tremor.current = null; }, []);
  useEffect(() => { if (calm) stopTremor(); }, [calm]); // eslint-disable-line react-hooks/exhaustive-deps

  const setProgress = (p: number) => { progress.current = p; btn.current?.style.setProperty('--hold', String(p)); };
  const begin = () => {
    if (active.current) return;
    active.current = true; setHolding(true);
    start.current = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start.current) / HOLD_MS);
      setProgress(p);
      if (p >= 1) { active.current = false; stopTremor(); onConfirm(); return; }
      raf.current = requestAnimationFrame(tick);
    };
    raf.current = requestAnimationFrame(tick);
    if (!calm && !tremor.current) {
      tremor.current = playLoop.register(() => {
        const el = book.current;
        if (!el) return;
        const a = TREMOR_PX * progress.current;
        el.style.transform = `translate(${((Math.random() * 2 - 1) * a).toFixed(2)}px, ${((Math.random() * 2 - 1) * a).toFixed(2)}px)`;
      });
    }
  };
  const end = () => {
    cancelAnimationFrame(raf.current);
    stopTremor();
    if (!active.current) return;
    active.current = false; setHolding(false); setProgress(0);
  };
  usePlayTarget(btn);
  const isHoldKey = (e: React.KeyboardEvent) => e.key === ' ' || e.key === 'Enter';

  return (
    <button ref={btn} className="book-delete" style={{ ['--hold' as any]: 0 }} aria-label="Hold to delete"
      onPointerDown={begin} onPointerUp={end} onPointerLeave={end} onPointerCancel={end} onBlur={end}
      onKeyDown={(e) => { if (isHoldKey(e)) { e.preventDefault(); if (!e.repeat) begin(); } }}
      onKeyUp={(e) => { if (isHoldKey(e)) { e.preventDefault(); end(); } }}>
      {holding ? 'Keep holding…' : 'Hold to delete'}
    </button>
  );
};

const BookOverlay: React.FC<{
  entry: Entry; reduce: boolean;
  onClose: () => void; onEdit: (e: Entry) => void; onDelete: (e: Entry) => void;
}> = ({ entry, reduce, onClose, onEdit, onDelete }) => {
  const urls = useMemo(() => entry.images.map((b) => URL.createObjectURL(b)), [entry.images]);
  useEffect(() => () => urls.forEach((u) => URL.revokeObjectURL(u)), [urls]);
  const date = useMemo(() => new Date(entry.date), [entry.date]);
  const text = entry.text.length ? entry.text : EMPTY_TEXT;
  const book = useRef<HTMLDivElement>(null);
  const q = useFpsBackoff();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <motion.div className="book-overlay" role="dialog" aria-modal="true" aria-label={`Entry from ${entry.location}`}
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={onClose}>
      <div className="book-stage" onClick={(e) => e.stopPropagation()} style={{ perspective: 1500 }}>
        <div ref={book} className="gj-book" style={{ ['--book' as any]: entry.textColors.locationColor || '#f2f0e9' }}>
          <div className="gj-book-back" aria-hidden />
          <div className="gj-page-wrap">
            <JournalCanvas date={date} location={entry.location} textSections={text}
              images={urls} textColors={entry.textColors} layoutMode="freeflow"
              savedImagePositions={entry.imageLayout ?? undefined} onNewEntry={NOOP} />
          </div>
          <div className="gj-book-spine" aria-hidden />
          {!reduce && (
            <motion.div className="gj-book-cover" aria-hidden initial={{ rotateY: 0 }} animate={{ rotateY: -180 }}
              transition={{ duration: 1.6, ease: [0.65, 0, 0.35, 1], delay: 0.2 }}>
              <span className="gj-cover-mark">m</span>
            </motion.div>
          )}
        </div>
        <div className="book-actions">
          <PlayButton onClick={onClose}>Close</PlayButton>
          <PlayButton onClick={() => onEdit(entry)}>Edit</PlayButton>
          <HoldDeleteButton onConfirm={() => onDelete(entry)} book={book} calm={reduce || q >= 2} />
        </div>
      </div>
    </motion.div>
  );
};

export default BookOverlay;
